import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
  User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { IAuthRepository, LoginCredentials, RegisterData } from '../interfaces/IAuthRepository';
import { User } from '../../types';

export class FirebaseAuthRepository implements IAuthRepository {
  async login(credentials: LoginCredentials): Promise<User> {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth!,
        credentials.email,
        credentials.password
      );

      let user = await this.getFirestoreUser(userCredential.user.uid);
      
      // Si l'utilisateur n'existe pas dans Firestore, le créer avec les données basiques
      if (!user) {
        const firebaseUser = userCredential.user;
        const userData: User = {
          id: firebaseUser.uid,
          email: firebaseUser.email || credentials.email,
          displayName: firebaseUser.displayName || undefined,
          role: 'user', // Par défaut, tous les nouveaux utilisateurs sont des users
        };

        // Créer le document utilisateur s'il n'existe pas (pour les anciens utilisateurs)
        try {
          await setDoc(doc(db!, 'users', firebaseUser.uid), {
            email: firebaseUser.email || credentials.email,
            displayName: firebaseUser.displayName || undefined,
            role: 'user',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (firestoreError) {
          console.error('Erreur lors de la création du document utilisateur:', firestoreError);
        }

        user = userData;
      }

      return user;
    } catch (error: any) {
      console.error('Erreur de connexion:', error);
      
      // Messages d'erreur plus explicites
      switch (error.code) {
        case 'auth/user-not-found':
          throw new Error('Aucun compte trouvé avec cet email');
        case 'auth/wrong-password':
          throw new Error('Mot de passe incorrect');
        case 'auth/invalid-email':
          throw new Error('Adresse email invalide');
        case 'auth/user-disabled':
          throw new Error('Ce compte a été désactivé');
        case 'auth/too-many-requests':
          throw new Error('Trop de tentatives. Réessayez plus tard');
        default:
          throw new Error('Erreur de connexion');
      }
    }
  }

  async register(data: RegisterData): Promise<User> {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth!,
        data.email,
        data.password
      );

      // Mettre à jour le profil Firebase Auth avec le nom complet
      await updateProfile(userCredential.user, {
        displayName: data.name,
      });

      // Retourner l'utilisateur avec le displayName
      const userData: User = {
        id: userCredential.user.uid,
        email: data.email,
        displayName: data.name,
        role: 'user'
      };

      // Créer le document utilisateur dans Firestore avec le rôle
      try {
        await setDoc(doc(db!, 'users', userCredential.user.uid), {
          email: data.email,
          displayName: data.name,
          role: 'user',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (firestoreError) {
        console.error('Erreur lors de la création du document utilisateur:', firestoreError);
        // On continue même si la création du document échoue
      }

      return userData;
    } catch (error: any) {
      console.error('Erreur d\'inscription:', error);
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          throw new Error('Un compte existe déjà avec cet email');
        case 'auth/invalid-email':
          throw new Error('Adresse email invalide');
        case 'auth/weak-password':
          throw new Error('Le mot de passe doit contenir au moins 6 caractères');
        case 'auth/operation-not-allowed':
          throw new Error('Inscription par email désactivée');
        default:
          throw new Error('Erreur lors de la création du compte');
      }
    }
  }

  async logout(): Promise<void> {
    try {
      await signOut(auth!);
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
      throw new Error('Erreur lors de la déconnexion');
    }
  }

  async getCurrentUser(): Promise<User | null> {
    const firebaseUser = auth!.currentUser;
    if (!firebaseUser) {
      return null;
    }

    // D'abord essayer de récupérer depuis Firestore (pour le champ admin)
    const firestoreUser = await this.getFirestoreUser(firebaseUser.uid);
    if (firestoreUser) {
      return firestoreUser;
    }

    // Si pas dans Firestore, créer un utilisateur basique depuis Firebase Auth
    return {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      displayName: firebaseUser.displayName || undefined,
      role: 'user',
    };
  }

  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    return firebaseOnAuthStateChanged(auth!, async (firebaseUser: FirebaseUser | null) => {
      if (firebaseUser) {
        try {
          // D'abord essayer de récupérer depuis Firestore (pour le champ admin)
          let user = await this.getFirestoreUser(firebaseUser.uid);
          
          if (!user) {
            // Si pas dans Firestore, créer un utilisateur basique depuis Firebase Auth
            user = {
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName || undefined,
              role: 'user',
            };
          }
          
          callback(user);
        } catch (error) {
          console.error('Erreur lors de la récupération des données utilisateur:', error);
          callback(null);
        }
      } else {
        callback(null);
      }
    });
  }

  async updateProfile(userId: string, data: Partial<User>): Promise<void> {
    try {
      const userRef = doc(db!, 'users', userId);
      
      await updateDoc(userRef, {
        ...data,
        updatedAt: new Date(),
      });

      // Mettre à jour aussi le profil Firebase Auth si nécessaire
      if (auth!.currentUser && (data.firstName || data.lastName)) {
        const user = await this.getFirestoreUser(userId);
        if (user) {
          await updateProfile(auth!.currentUser, {
            displayName: `${user.firstName} ${user.lastName}`,
          });
        }
      }
    } catch (error) {
      console.error('Erreur de mise à jour du profil:', error);
      throw new Error('Erreur lors de la mise à jour du profil');
    }
  }

  async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth!, email);
    } catch (error: any) {
      console.error('Erreur de réinitialisation:', error);
      
      switch (error.code) {
        case 'auth/user-not-found':
          // Ne pas révéler si l'email existe pour la sécurité
          break;
        case 'auth/invalid-email':
          throw new Error('Adresse email invalide');
        default:
          throw new Error('Erreur lors de l\'envoi de l\'email');
      }
    }
  }

  private async getFirestoreUser(uid: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db!, 'users', uid));
      
      if (!userDoc.exists()) {
        return null;
      }

      const userData = userDoc.data();
      const firebaseUser = auth!.currentUser;
      
      return {
        id: uid,
        email: userData.email || firebaseUser?.email || '',
        displayName: firebaseUser?.displayName || userData.displayName || undefined,
        firstName: userData.firstName, // Pour compatibilité
        lastName: userData.lastName, // Pour compatibilité
        role: userData.role || 'user',
        zones: userData.zones || [],
        phone: userData.phone || undefined,
      };
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'utilisateur:', error);
      return null;
    }
  }
}